import { getType } from 'typesafe-actions';

import {
  DairyRevenueProtectionActions,
  dairyRevenueProtectionActions
} from '../../dairyRevenueProtection/actions';
import type {
  ClassPriceWeightingFactorRestrictedValue,
  ComponentPriceWeightingFactorRestrictedValue
} from '../../dairyRevenueProtection/settings/types';
import { ScenarioAnalysisActions, scenarioAnalysisActions } from '../actions';

export interface EndorsementModalSettings {
  state: number;
  quarter: string;
  canSell: boolean;
  canSellNote: string;
  declaredMilkProduction: number;
  salesEffectiveDate: Date;
  calendarDate: Date;
  coverageLevel: number;
  declaredButterfatTest: number;
  protectionFactor: number;
  declaredProteinTest: number;
  declaredClassPriceWeightingFactor: number;
  declaredComponentPriceWeightingFactor: number;
  quarterList: Array<{
    quarter: string;
    practiceCode: number;
    proteinTest: number;
    butterfatTest: number;
    componentPriceWeightingFactorRestrictedValue: ComponentPriceWeightingFactorRestrictedValue;
    classPriceWeightingFactorRestrictedValue: ClassPriceWeightingFactorRestrictedValue;
  }>;
  isSalesDateAvailable: boolean;
  expectedRevenue: { component: number; class: number };
  revenueGuarantee: { component: number; class: number };
  producerPremium: { component: number; class: number };
  expectedRevenueCwt: { component: number; class: number };
  revenueGuaranteeCwt: { component: number; class: number };
  producerPremiumCwt: { component: number; class: number };
  reinsuranceYear: number;
  share: number;
}

export const initialState: EndorsementModalSettings = {
  state: 6,
  quarter: '',
  canSell: false,
  canSellNote: '',
  declaredMilkProduction: 10000000,
  salesEffectiveDate: new Date(),
  calendarDate: new Date(),
  coverageLevel: 0.95,
  declaredButterfatTest: 3.9,
  protectionFactor: 1,
  declaredProteinTest: 3.2,
  declaredClassPriceWeightingFactor: 1,
  declaredComponentPriceWeightingFactor: 1,
  quarterList: [],
  isSalesDateAvailable: true,
  expectedRevenue: { component: 0, class: 0 },
  revenueGuarantee: { component: 0, class: 0 },
  producerPremium: { component: 0, class: 0 },
  expectedRevenueCwt: { component: 0, class: 0 },
  revenueGuaranteeCwt: { component: 0, class: 0 },
  producerPremiumCwt: { component: 0, class: 0 },
  reinsuranceYear: 0,
  share: 1
};

export const endorsementSettingsReducer = (
  state = initialState,
  action: ScenarioAnalysisActions | DairyRevenueProtectionActions
) => {
  switch (action.type) {
    case getType(scenarioAnalysisActions.endorsementModal.setModalCoverageLevel):
      return { ...state, coverageLevel: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredButterfatTest):
      return { ...state, declaredButterfatTest: action.payload };
    case getType(
      scenarioAnalysisActions.endorsementModal.setModalDeclaredClassPriceWeightingFactor
    ):
      return { ...state, declaredClassPriceWeightingFactor: action.payload };
    case getType(
      scenarioAnalysisActions.endorsementModal.setModalDeclaredComponentPriceWeightingFactor
    ):
      return { ...state, declaredComponentPriceWeightingFactor: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredMilkProduction):
      return { ...state, declaredMilkProduction: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalDeclaredProteinTest):
      return { ...state, declaredProteinTest: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalProtectionFactor):
      return { ...state, protectionFactor: action.payload };
    case getType(dairyRevenueProtectionActions.settings.setCanSell):
      return { ...state, canSell: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setScenarioEndorsementQuarter):
      return { ...state, quarter: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalShare):
      return { ...state, share: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalQuarter): {
      const allQuarterData = [...state.quarterList];
      const defaultQuarterData = allQuarterData.find(q => q.quarter === action.payload);
      return {
        ...state,
        declaredButterfatTest: defaultQuarterData
          ? defaultQuarterData.butterfatTest
          : state.declaredButterfatTest,
        declaredProteinTest: defaultQuarterData
          ? defaultQuarterData.proteinTest
          : state.declaredProteinTest,
        quarter: action.payload,
        declaredComponentPriceWeightingFactor: state.declaredComponentPriceWeightingFactor
      };
    }
    case getType(scenarioAnalysisActions.endorsementModal.setModalCalendarDate):
      return { ...state, calendarDate: action.payload };
    case getType(
      scenarioAnalysisActions.endorsementModal.setModalSalesEffectiveDateWithoutPracticeFetch
    ):
      return { ...state, salesEffectiveDate: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalState):
      return { ...state, state: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setShare):
      return { ...state, share: action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.setModalData):
      return { ...state, ...action.payload };
    case getType(scenarioAnalysisActions.endorsementModal.initModalPracticeData): {
      const defaults = action.payload.practices.length
        ? action.payload.practices[0]
        : {
            butterfatTest: 3.9,
            proteinTest: 3.2,
            practiceCode: 0,
            componentPriceWeightingFactorRestrictedValue: null,
            classPriceWeightingFactorRestrictedValue: null
          };
      return {
        ...state,
        coverageLevel: action.payload.preferredCoverageLevel || 0.95,
        declaredButterfatTest: defaults.butterfatTest || 3.9,
        protectionFactor: action.payload.preferredProtectionLevel || 1,
        declaredProteinTest: defaults.proteinTest || 3.2,
        declaredClassPriceWeightingFactor: action.payload.preferredClassPriceWeightingFactor || 1,
        declaredComponentPriceWeightingFactor:
          action.payload.preferredComponentPriceWeightingFactor,
        state: Number(action.payload.stateCode),

        canSell: action.payload.canSell,
        canSellNote: action.payload.canSellNote,
        salesEffectiveDate: action.payload.salesEffectiveDate,
        calendarDate: action.payload.calendarDate,
        quarterList: action.payload.practices,
        reinsuranceYear: action.payload.reinsuranceYear,
        share: action.payload.share ?? 1
      };
    }
    case getType(scenarioAnalysisActions.endorsementModal.setModalPracticeData):
      return {
        ...state,
        ...action.payload
      };
    case getType(dairyRevenueProtectionActions.settings.updatePractice):
      return {
        ...state,
        quarterList: action.payload.practices,
        canSell: action.payload.canSell
      };
    case getType(scenarioAnalysisActions.endorsementModal.populateStateWithExisitingEndorsement): {
      return {
        ...state,
        coverageLevel: action.payload.coverageLevel,
        declaredButterfatTest: action.payload.butterfatTest,
        protectionFactor: action.payload.protectionFactor,
        declaredProteinTest: action.payload.proteinTest,
        declaredClassPriceWeightingFactor: action.payload.classPriceWeightingFactor,
        declaredComponentPriceWeightingFactor: action.payload.componentPriceWeightingFactor,
        declaredMilkProduction: action.payload.milkProduction,
        state: Number(action.payload.stateCode),
        salesEffectiveDate: new Date(action.payload.salesEffectiveDate),
        calendarDate: new Date(action.payload.salesEffectiveDate),
        reinsuranceYear: action.payload.reinsuranceYear
      };
    }
    case getType(dairyRevenueProtectionActions.settings.updateIsSalesDateAvailable): {
      return { ...state, isSalesDateAvailable: action.payload };
    }
    case getType(scenarioAnalysisActions.endorsementModal.setIsSalesDateAvailable): {
      return { ...state, isSalesDateAvailable: action.payload };
    }
    default:
      return state;
  }
};
